/**
 * Бланк декларації з податку на прибуток підприємств (J0100130) — візуально наближений до
 * ОФІЦІЙНОЇ друкованої форми (ч.1389, зауваження власника: «бухгалтери бояться скороченого
 * вигляду — треба повні форми, як у декларації з ПДВ і Податковому розрахунку»): шрифт із
 * зарубками, чорні рамки, шапка блоками 1–10 (тип, період, платник, податковий номер, КВЕД,
 * адреса, контролюючий орган, особливі відмітки), усі рядки 01–47 з офіційними назвами,
 * відмітки додатків і форм фінзвітності, рішення про незастосування різниць, підписи.
 *
 * Використовується РЕДАКТОРОМ (клітинки редаговані, жовтий фон; похідні — сірі) і сторінкою
 * ДРУКУ (print — значення текстом). Стилі — спільні з бланком ПДВ (VatDeclarationBlank).
 */
import type { CSSProperties, ReactNode } from 'react';
import type { Organization } from '@/api/catalogs';
import {
  MAIN_ROWS, SPECIAL_MARKS,
  type ProfitTaxAppendixData, type ProfitTaxDeclaration, type ProfitTaxFields, type RiRow,
} from '@/api/profitTaxDeclarations';
import { B, SERIF, XMark, blankBox, nameCell, numCell, thBlank, valCell } from './VatDeclarationBlank';
import { chiefForReports } from '@/shared/utils/orgOfficials';

export const PERIOD_MARKS: { q: number; code: string; title: string }[] = [
  { q: 1, code: 'H1KV', title: 'І квартал' }, { q: 2, code: 'HHY', title: 'Півріччя' },
  { q: 3, code: 'H3KV', title: 'Три квартали' }, { q: 4, code: 'HY', title: 'Рік' },
];

export const fmt0 = (v: string | number | null | undefined): string => {
  if (v == null || v === '') return '';
  const n = Number(v);
  return Number.isFinite(n) ? n.toLocaleString('uk-UA', { maximumFractionDigits: 0 }) : String(v);
};

export const ro = (v: string | number | null | undefined, align: CSSProperties['textAlign'] = 'left', print?: boolean): ReactNode => (
  <span style={{ display: 'block', padding: '2px 4px', fontWeight: 600, textAlign: align, fontFamily: SERIF,
    fontSize: '0.82rem', background: print ? 'transparent' : '#eef1f4', color: '#111', minHeight: '1.1em' }}>
    {v == null || v === '' ? ' ' : v}
  </span>
);

/** Клітинка суми бланка (гривні без копійок): редагована — жовта, похідна чи друк — текстом. */
export function SumCell({ value, onChange, readOnly, print, label }: {
  value: string | undefined; onChange?: (raw: string) => void; readOnly: boolean; print?: boolean; label?: string;
}) {
  if (readOnly || !onChange) {
    return (
      <span style={{ display: 'block', padding: '2px 4px', textAlign: 'right', fontFamily: SERIF, fontSize: '0.82rem',
        fontWeight: 600, background: print ? 'transparent' : '#eef1f4', minHeight: '1.1em', color: '#111' }}>
        {fmt0(value)}
      </span>
    );
  }
  return (
    <input type="number" step="1" value={value ?? ''} aria-label={label}
      onChange={(e) => onChange(e.target.value)}
      style={{ width: '100%', boxSizing: 'border-box', border: 'none', outline: 'none', fontFamily: SERIF,
        fontSize: '0.82rem', textAlign: 'right', padding: '2px 4px', background: '#fefce8', color: '#111' }} />
  );
}

/** Текстове поле бланка (реквізити додатків): редаговане — жовте, інакше — текст. */
export function TextCell({ value, onChange, readOnly, print, label, align = 'left' }: {
  value: string | undefined | null; onChange?: (raw: string) => void; readOnly: boolean; print?: boolean;
  label?: string; align?: 'left' | 'center' | 'right';
}) {
  if (readOnly || !onChange) return <>{ro(value, align, print)}</>;
  return (
    <input value={value ?? ''} aria-label={label} onChange={(e) => onChange(e.target.value)}
      style={{ width: '100%', boxSizing: 'border-box', border: 'none', outline: 'none', fontFamily: SERIF,
        fontSize: '0.82rem', textAlign: align, padding: '2px 4px', background: '#fefce8', color: '#111' }} />
  );
}

/** Відмітка «X» бланка; у редакторі — клікабельна. */
export function Mark({ on, onToggle, readOnly, title }: { on: boolean; onToggle?: () => void; readOnly?: boolean; title?: string }) {
  return (
    <span onClick={() => { if (!readOnly && onToggle) onToggle(); }} title={title}
      style={{ cursor: !readOnly && onToggle ? 'pointer' : 'default', display: 'inline-block' }}>
      <XMark on={on} />
    </span>
  );
}

export const small: CSSProperties = { fontSize: '0.66rem', color: '#444', fontFamily: SERIF };
export const label: CSSProperties = { fontFamily: SERIF, fontSize: '0.8rem', color: '#111' };
const blockNo: CSSProperties = { ...numCell, width: 28, fontWeight: 700 };

export function typeLabel(t: ProfitTaxDeclaration['report_type']): string {
  return t === 'REPORTING' ? 'Звітна' : t === 'REPORTING_NEW' ? 'Звітна нова' : 'Уточнююча';
}

/** Період, що уточнюється: з уточнюючої або з першого додатка ВП. */
export function correctedPeriod(doc: ProfitTaxDeclaration): { year: number; quarter: number } | null {
  if (doc.report_type === 'CORRECTING' && doc.corrected_period_year) {
    return { year: doc.corrected_period_year, quarter: doc.corrected_period_quarter ?? 4 };
  }
  const vp = doc.appendix_data?.vp?.[0];
  return vp ? { year: vp.year, quarter: vp.quarter } : null;
}

/** Рядок відміток періоду бланка: «І квартал | Півріччя | Три квартали | Рік» + рік. */
export function PeriodMarks({ quarter, year, yearLabel = 'року' }: { quarter: number | null; year: number | string | null; yearLabel?: string }) {
  return (
    <div style={{ display: 'flex', gap: 14, alignItems: 'center', flexWrap: 'wrap', ...label }}>
      {PERIOD_MARKS.map((m) => <span key={m.code}><XMark on={quarter === m.q} /> {m.title}</span>)}
      <span style={{ marginLeft: 'auto' }}>
        <span style={{ display: 'inline-block', minWidth: 54, borderBottom: B, textAlign: 'center', fontWeight: 700 }}>{year ?? ' '}</span> {yearLabel}
      </span>
    </div>
  );
}

/** Шапка ОСНОВНОЇ декларації — блоки 1–10 за офіційним бланком. */
export function DeclarationHeader({ doc, org, fields, onFields, readOnly, print }: {
  doc: ProfitTaxDeclaration; org: Organization | undefined; fields: ProfitTaxFields;
  onFields?: (patch: Partial<ProfitTaxFields>) => void; readOnly: boolean; print?: boolean;
}) {
  const corr = correctedPeriod(doc);
  const marks = new Set(fields.marks ?? []);
  const toggleMark = (code: string) => {
    const next = new Set(marks);
    if (next.has(code)) next.delete(code); else next.add(code);
    onFields?.({ marks: Array.from(next) });
  };
  const editable = !readOnly && !!onFields;
  return (
    <div style={{ ...blankBox }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', padding: '4px 6px 0', ...small }}>
        <span>Форма J0100130</span>
        <span style={{ textAlign: 'right' }}>ЗАТВЕРДЖЕНО<br />Наказ Міністерства фінансів України<br />20 жовтня 2015 року № 897 (у редакції наказу від 20 лютого 2023 року № 101)</span>
      </div>
      <table style={{ width: '100%', borderCollapse: 'collapse', marginTop: 4 }}>
        <colgroup><col style={{ width: 28 }} /><col /><col style={{ width: 330 }} /></colgroup>
        <tbody>
          <tr>
            <td style={blockNo}>1</td>
            <td style={{ ...nameCell, textAlign: 'center', fontWeight: 700, fontSize: '0.95rem' }}>
              Податкова декларація<br />з податку на прибуток підприємств
            </td>
            <td style={{ ...nameCell }}>
              <div style={{ display: 'grid', gridTemplateColumns: 'auto 1fr', gap: '3px 8px', alignItems: 'center', ...label }}>
                <XMark on={doc.report_type === 'REPORTING'} /><span>Звітна</span>
                <XMark on={doc.report_type === 'REPORTING_NEW'} /><span>Звітна нова</span>
                <XMark on={doc.report_type === 'CORRECTING'} /><span>Уточнююча</span>
              </div>
            </td>
          </tr>
          <tr>
            <td style={blockNo}>2</td>
            <td style={nameCell}>Звітний (податковий) період</td>
            <td style={nameCell}>
              <PeriodMarks quarter={doc.period_quarter} year={doc.period_year} />
              <div style={{ display: 'flex', gap: 14, marginTop: 4, ...label }}>
                <span><XMark on={fields.base_period === 'QUARTER'} /> Базовий звітний період квартал</span>
                <span><XMark on={fields.base_period !== 'QUARTER'} /> Базовий звітний період рік</span>
              </div>
            </td>
          </tr>
          <tr>
            <td style={blockNo}>3</td>
            <td style={nameCell}>Звітний (податковий) період, що уточнюється</td>
            <td style={nameCell}><PeriodMarks quarter={corr?.quarter ?? null} year={corr?.year ?? null} /></td>
          </tr>
          <tr>
            <td style={blockNo}>4</td>
            <td style={nameCell} colSpan={2}>
              Платник: {ro(org?.full_name || org?.name, 'left', print)}
              <div style={{ ...small, textAlign: 'center' }}>(повне найменування платника податку згідно з реєстраційними документами)</div>
            </td>
          </tr>
          <tr>
            <td style={blockNo}>5</td>
            <td style={nameCell}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 8 }}>
                <div>Податковий номер або серія (за наявності) та номер паспорта{ro(org?.edrpou, 'center', print)}</div>
                <div>Код виду економічної діяльності (КВЕД)
                  <TextCell value={fields.kved ?? org?.kved_main ?? ''} readOnly={!editable} print={print} align="center" label="КВЕД"
                    onChange={(v) => onFields?.({ kved: v })} />
                </div>
                <div>Поштовий індекс
                  <TextCell value={fields.zip ?? ''} readOnly={!editable} print={print} align="center" label="Поштовий індекс"
                    onChange={(v) => onFields?.({ zip: v })} />
                </div>
              </div>
            </td>
            <td style={nameCell}>
              Телефон {ro(org?.phone, 'left', print)}
              E-mail {ro(org?.email, 'left', print)}
            </td>
          </tr>
          <tr>
            <td style={blockNo}>6</td>
            <td style={nameCell} colSpan={2}>Податкова адреса {ro(org?.legal_address || org?.actual_address, 'left', print)}</td>
          </tr>
          <tr>
            <td style={blockNo}>7</td>
            <td style={nameCell} colSpan={2}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, fontSize: '0.74rem' }}>
                <div>Повне найменування нерезидента{ro('', 'left', print)}Місцезнаходження нерезидента{ro('', 'left', print)}</div>
                <div>Назва країни резиденції нерезидента (за Класифікацією країн світу, українською мовою){ro('', 'left', print)}Код країни резиденції{ro('', 'center', print)}</div>
              </div>
              <div style={small}>(заповнюється лише постійним представництвом / відокремленим підрозділом нерезидента)</div>
            </td>
          </tr>
          <tr>
            <td style={blockNo}>8</td>
            <td style={nameCell} colSpan={2}>
              {ro(org?.sti_name, 'left', print)}
              <div style={{ ...small, textAlign: 'center' }}>(найменування контролюючого органу, до якого подається Податкова декларація з податку на прибуток підприємств)</div>
            </td>
          </tr>
          <tr>
            <td style={blockNo}>9</td>
            <td style={nameCell} colSpan={2}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, fontSize: '0.74rem' }}>
                <div>Повне найменування інституту спільного інвестування{ro('', 'left', print)}</div>
                <div>Реєстраційний код інституту спільного інвестування{ro('', 'center', print)}</div>
              </div>
            </td>
          </tr>
          <tr>
            <td style={blockNo}>10</td>
            <td style={nameCell} colSpan={2}>
              <b>Особливі відмітки.</b> Податкова декларація платника податку на прибуток підприємств:
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '2px 14px', marginTop: 3 }}>
                {SPECIAL_MARKS.filter((m) => !print || marks.has(m.code)).map((m) => (
                  <div key={m.code} style={{ display: 'flex', gap: 6, alignItems: 'flex-start', ...label, fontSize: '0.74rem' }}>
                    <Mark on={marks.has(m.code)} readOnly={!editable} onToggle={() => toggleMark(m.code)} title={m.code} />
                    <span>{m.title}</span>
                  </div>
                ))}
                {print && marks.size === 0 && <span style={small}>відміток немає</span>}
              </div>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  );
}

const DEFAULT_RATES: Record<string, number> = { R006G1: 18, R0064G1: 18, R008G1: 3, R010G1: 30, R012G1: 10, R015G1: 18 };

/** Підставити ставки у назву рядка: «× {R006G1} / 100» → «× 18 / 100». */
export function withRates(title: string, values: Record<string, string>): string {
  return title.replace(/\{(R\d+G1)\}/g, (_m, code: string) => {
    const n = Number(values[code] ?? DEFAULT_RATES[code] ?? 18);
    return Number.isFinite(n) ? String(Number(n.toFixed(2))) : String(values[code]);
  });
}

/** Усі рядки основної частини (01–47) з підзаголовками розділів — як у бланку. */
export function MainRows({ values, setV, readOnly, print, rows = MAIN_ROWS }: {
  doc?: ProfitTaxDeclaration;
  values: Record<string, string>;
  setV?: (code: string, raw: string) => void;
  readOnly?: boolean;
  print?: boolean;
  rows?: typeof MAIN_ROWS;
}) {
  return (
    <table style={{ width: '100%', borderCollapse: 'collapse', ...blankBox, marginTop: 6 }}>
      <colgroup><col /><col style={{ width: 70 }} /><col style={{ width: 140 }} /></colgroup>
      <thead>
        <tr>
          <th style={{ ...thBlank, textAlign: 'right', border: 'none', fontSize: '0.7rem' }} colSpan={3}>(грн)</th>
        </tr>
        <tr>
          <th style={thBlank}>Показники</th><th style={thBlank}>Код рядка</th><th style={thBlank}>Сума</th>
        </tr>
        <tr>
          <th style={thBlank}>1</th><th style={thBlank}>2</th><th style={thBlank}>3</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((r, i) => {
          if (r.heading) {
            return (
              <tr key={`h${i}`}>
                <td colSpan={3} style={{ ...nameCell, textAlign: 'center', fontWeight: 700, background: print ? '#fff' : '#f8f8f6' }}>{r.heading}</td>
              </tr>
            );
          }
          const auto = !!r.auto;
          return (
            <tr key={r.field} style={{ fontWeight: auto ? 700 : 400 }}>
              <td style={nameCell}>{withRates(r.title, values)}</td>
              <td style={numCell}>{r.row}</td>
              <td style={valCell}>
                <SumCell value={values[r.field]} readOnly={!!readOnly || auto || !setV} print={print} label={`${r.row} ${r.title}`}
                  onChange={setV ? (raw) => setV(r.field, raw) : undefined} />
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}

const APPENDIX_FLAGS: { key: string; label: string; code: string }[] = [
  { key: 'av', label: 'АВ', code: 'HD1' }, { key: 'zp', label: 'ЗП', code: 'HD2' }, { key: 'pn', label: 'ПН', code: 'HD3' },
  { key: 'tc', label: 'ТЦ', code: 'HD4' }, { key: 'vp', label: 'ВП', code: 'HD5' }, { key: 'ri', label: 'РІ', code: 'HD6' },
  { key: 'pz', label: 'ПЗ', code: 'HD7' }, { key: 'am', label: 'АМ', code: 'HD8' }, { key: 'cp', label: 'ЦП', code: 'HD9' },
  { key: 'pp', label: 'ПП', code: 'HD10' }, { key: 'kik', label: 'КІК', code: 'HD13' }, { key: 'mpz', label: 'МПЗ', code: 'HD14' },
  { key: 'diya', label: 'ДІЯ', code: 'HD15' }, { key: 'shchav', label: 'ЩАВ', code: 'HD16' }, { key: 'bd', label: 'БД', code: 'HD17' },
  { key: 'ep', label: 'ЄП', code: 'HD18' },
];
const FS_FLAGS: { code: string; label: string; forms: string[] }[] = [
  { code: 'HF1', label: 'Баланс (Звіт про фінансовий стан)', forms: ['1'] },
  { code: 'HF2', label: 'Звіт про фінансові результати (Звіт про сукупний дохід)', forms: ['2'] },
  { code: 'HF3', label: 'Звіт про рух грошових коштів', forms: ['3'] },
  { code: 'HF4', label: 'Звіт про власний капітал', forms: ['4'] },
  { code: 'HF5', label: 'Примітки до річної фінансової звітності', forms: [] },
  { code: 'HF6', label: 'Фінансовий звіт субʼєкта малого підприємництва: Баланс', forms: ['1m'] },
  { code: 'HF7', label: 'Фінансовий звіт субʼєкта малого підприємництва: Звіт про фінансові результати', forms: ['2m'] },
  { code: 'HF8', label: 'Спрощений фінансовий звіт субʼєкта малого підприємництва: Баланс', forms: ['1ms'] },
  { code: 'HF9', label: 'Спрощений фінансовий звіт субʼєкта малого підприємництва: Звіт про фінансові результати', forms: ['2ms'] },
];

export function appendixIncluded(a: ProfitTaxAppendixData, key: string): boolean {
  if (key === 'pn') return (a.pn?.length ?? 0) > 0;
  if (key === 'vp') return (a.vp?.length ?? 0) > 0;
  return !!(a as Record<string, unknown>)[`include_${key}`];
}

/** Кількість аркушів додатків: по одному на кожен додаток і файл фінзвітності. */
function appendixSheets(appendix: ProfitTaxAppendixData, statementForms: string[]): number {
  return ['ri', 'am', 'zp', 'av'].filter((k) => appendixIncluded(appendix, k)).length
    + (appendix.pn?.length ?? 0) + (appendix.vp?.length ?? 0) + statementForms.length;
}

/** Відмітки внизу бланка: додатки, форми фінзвітності, аудит, доповнення, рішення про різниці. */
export function MarksBlock({ appendix, fields, statementForms, statementStandards, onFields, readOnly, print }: {
  appendix: ProfitTaxAppendixData; fields: ProfitTaxFields;
  /** форми привʼязаної фінзвітності ('1','2','3','4' | '1m','2m' | '1ms','2ms') */
  statementForms: string[]; statementStandards?: 'NPSBO' | 'IFRS' | null;
  onFields?: (patch: Partial<ProfitTaxFields>) => void; readOnly: boolean; print?: boolean;
}) {
  const editable = !readOnly && !!onFields;
  const decision = !fields.apply_differences;
  const sheets = appendixSheets(appendix, statementForms);
  return (
    <table style={{ width: '100%', borderCollapse: 'collapse', ...blankBox, marginTop: 6 }}>
      <tbody>
        <tr>
          <td style={{ ...nameCell, width: 120 }}>Наявність додатків</td>
          <td style={nameCell}>
            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
              {APPENDIX_FLAGS.map((f) => (
                <span key={f.code} style={{ ...label, whiteSpace: 'nowrap' }} title={f.code}>{f.label} <XMark on={appendixIncluded(appendix, f.key)} /></span>
              ))}
              <span style={{ marginLeft: 'auto', ...label, whiteSpace: 'nowrap' }}>
                ФЗ: НП(С)БО <XMark on={statementForms.length > 0 && statementStandards !== 'IFRS'} />
                {' '}МСФЗ <XMark on={statementForms.length > 0 && statementStandards === 'IFRS'} />
              </span>
            </div>
          </td>
        </tr>
        <tr>
          <td style={nameCell}>Наявність поданих до Податкової декларації додатків — форм фінансової звітності</td>
          <td style={nameCell}>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '2px 10px' }}>
              {FS_FLAGS.map((f) => (
                <span key={f.code} style={{ ...label, fontSize: '0.74rem' }} title={f.code}>
                  <XMark on={f.forms.some((c) => statementForms.includes(c))} /> {f.label}
                </span>
              ))}
            </div>
          </td>
        </tr>
        <tr>
          <td style={{ ...nameCell, textAlign: 'center' }}>
            <Mark on={!!fields.audit_publish} readOnly={!editable} onToggle={() => onFields?.({ audit_publish: !fields.audit_publish })} title="H10" />
          </td>
          <td style={{ ...nameCell, fontSize: '0.74rem' }}>відмітка про обовʼязок платника відповідно до Закону України «Про бухгалтерський облік та фінансову звітність в Україні» оприлюднювати річну фінансову звітність та річну консолідовану фінансову звітність разом з аудиторським звітом</td>
        </tr>
        <tr>
          <td style={nameCell}>Наявність доповнення <XMark on={!!(fields.supplement_text ?? '').trim()} /></td>
          <td style={nameCell}>
            <div style={small}>Доповнення до Податкової декларації (заповнюється і подається відповідно до пункту 46.4 статті 46 розділу ІІ Податкового кодексу України)</div>
            {editable
              ? <textarea rows={2} value={fields.supplement_text ?? ''} onChange={(e) => onFields?.({ supplement_text: e.target.value })} aria-label="Доповнення до декларації"
                  style={{ width: '100%', boxSizing: 'border-box', border: 'none', background: '#fefce8', fontFamily: SERIF, fontSize: '0.8rem', color: '#111' }} />
              : ro(fields.supplement_text ?? '', 'left', print)}
          </td>
        </tr>
        <tr>
          <td style={nameCell}>Наявність рішення <XMark on={decision} /></td>
          <td style={nameCell}>
            <div style={small}>Прийнято рішення про незастосування коригувань фінансового результату до оподаткування на усі різниці (крім відʼємного значення обʼєкта оподаткування минулих податкових (звітних) років)</div>
            {decision && (editable
              ? <textarea rows={3} value={fields.decision_text ?? ''} onChange={(e) => onFields?.({ decision_text: e.target.value })} aria-label="Текст рішення"
                  style={{ width: '100%', boxSizing: 'border-box', border: 'none', background: '#fefce8', fontFamily: SERIF, fontSize: '0.8rem', color: '#111' }} />
              : ro(fields.decision_text ?? '', 'left', print))}
            {!decision && <div style={small}>коригування на різниці застосовуються (додаток РІ)</div>}
          </td>
        </tr>
        <tr>
          <td style={nameCell} colSpan={2}>
            Додатки на <span style={{ display: 'inline-block', minWidth: 40, borderBottom: B, textAlign: 'center', fontWeight: 700 }}>{sheets || ' '}</span> арк.
          </td>
        </tr>
      </tbody>
    </table>
  );
}

/** Підписи внизу бланка (декларація і додатки). */
export function SignaturesBlock({ doc, org, print, withInn = true }: {
  doc: ProfitTaxDeclaration; org: Organization | undefined; print?: boolean; withInn?: boolean;
}) {
  const d = doc.document_date ? new Date(doc.document_date) : null;
  const dateStr = d ? d.toLocaleDateString('uk-UA') : '';
  const chief = org ? chiefForReports(org) : null;
  return (
    <table style={{ width: '100%', borderCollapse: 'collapse', ...blankBox, marginTop: 6 }}>
      <tbody>
        <tr>
          <td style={{ ...nameCell, width: 110 }}>Дата подання</td>
          <td style={{ ...valCell, width: 110 }}>{ro(dateStr, 'center', print)}</td>
          <td style={nameCell}>Керівник (уповноважена особа)</td>
          <td style={{ ...valCell, width: 220 }}>{ro(org?.director_name, 'left', print)}</td>
          {withInn && <><td style={{ ...nameCell, width: 90 }}>РНОКПП</td><td style={{ ...valCell, width: 120 }}>{ro(org?.director_inn ?? null, 'center', print)}</td></>}
        </tr>
        {chief ? (
          <tr>
            <td style={nameCell} colSpan={2}><span style={small}>М. П. (за наявності)</span></td>
            <td style={nameCell}>Головний бухгалтер (особа, відповідальна за ведення бухгалтерського обліку)</td>
            <td style={valCell}>{ro(org?.chief_accountant, 'left', print)}</td>
            {withInn && <><td style={nameCell}>РНОКПП</td><td style={valCell}>{ro(org?.chief_accountant_inn ?? null, 'center', print)}</td></>}
          </tr>
        ) : null}
      </tbody>
    </table>
  );
}

/** Шапка ДОДАТКА (спільна для РІ, АМ, ЗП, АВ, ПН, ВП): назва, тип, податковий номер, період. */
export function AppendixHeader({ doc, org, title, subtitle, print, corrected, noCorrectingMark }: {
  doc: ProfitTaxDeclaration; org: Organization | undefined; title: string; subtitle?: string; print?: boolean;
  /** ВП: уточнюваний період другим рядком */
  corrected?: { year: number; quarter: number } | null;
  noCorrectingMark?: boolean;
}) {
  return (
    <table style={{ width: '100%', borderCollapse: 'collapse', ...blankBox, marginBottom: 6 }}>
      <tbody>
        <tr>
          <td style={{ ...nameCell, width: '55%' }}>
            <b>{title}</b>{subtitle ? <><br /><span style={small}>{subtitle}</span></> : null}
          </td>
          <td style={nameCell}>
            <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap', ...label }}>
              <span><XMark on={doc.report_type === 'REPORTING'} /> Звітна</span>
              <span><XMark on={doc.report_type === 'REPORTING_NEW'} /> Звітна нова</span>
              {noCorrectingMark ? null : <span><XMark on={doc.report_type === 'CORRECTING'} /> Уточнююча</span>}
            </div>
          </td>
        </tr>
        <tr>
          <td style={nameCell}>Податковий номер або серія (за наявності) та номер паспорта {ro(org?.edrpou, 'center', print)}</td>
          <td style={nameCell}>
            <div style={label}>Звітний (податковий) період</div>
            <PeriodMarks quarter={doc.period_quarter} year={doc.period_year} />
            {corrected !== undefined && (
              <>
                <div style={{ ...label, marginTop: 4 }}>Звітний (податковий) період, що уточнюється</div>
                <PeriodMarks quarter={corrected?.quarter ?? null} year={corrected?.year ?? null} />
              </>
            )}
          </td>
        </tr>
      </tbody>
    </table>
  );
}

/** Додаток РІ — дві колонки бланка: ліворуч різниці, що збільшують, праворуч — що зменшують. */
export function RiAppendix({ rows, ri, setRi, readOnly, print, autoFields, doc, org }: {
  rows: RiRow[];
  ri: Record<string, string>;
  setRi?: (code: string, raw: string) => void;
  readOnly?: boolean;
  print?: boolean;
  /** ч.1388: клітинки, заповнені сервісом з обліку (амортизація, залишкова вартість вибулих) */
  autoFields?: string[];
  doc?: ProfitTaxDeclaration; org?: Organization;
}) {
  const editable = !print && !readOnly && !!setRi;
  const sections: { section: string; inc: RiRow[]; dec: RiRow[] }[] = [];
  for (const r of rows) {
    if (r.side === 'total') continue;
    let s = sections.find((x) => x.section === r.section);
    if (!s) { s = { section: r.section, inc: [], dec: [] }; sections.push(s); }
    (r.side === 'increase' ? s.inc : s.dec).push(r);
  }
  const cell = (r: RiRow | undefined) => {
    if (!r) return <><td style={numCell} /><td style={nameCell} /><td style={valCell} /></>;
    return (
      <>
        <td style={numCell}>{r.row}</td>
        <td style={{ ...nameCell, fontSize: '0.74rem' }}>
          {r.title}{autoFields?.includes(r.field) && !print && <span style={{ color: '#2563eb' }}> (з обліку)</span>}
        </td>
        <td style={valCell}>
          <SumCell value={ri[r.field]} readOnly={!editable} print={print} label={`РІ ${r.row}`} onChange={(raw) => setRi?.(r.field, raw)} />
        </td>
      </>
    );
  };
  return (
    <div>
      {doc && (
        <AppendixHeader doc={doc} org={org} print={print} title="Додаток РІ до рядка 03 РІ Податкової декларації з податку на прибуток підприємств"
          subtitle="Різниці" />
      )}
      <table style={{ width: '100%', borderCollapse: 'collapse', ...blankBox }}>
        <colgroup>
          <col style={{ width: 60 }} /><col /><col style={{ width: 120 }} />
          <col style={{ width: 60 }} /><col /><col style={{ width: 120 }} />
        </colgroup>
        <thead>
          <tr>
            <th style={thBlank} colSpan={3}>Різниці, на які збільшується фінансовий результат</th>
            <th style={thBlank} colSpan={3}>Різниці, на які зменшується фінансовий результат</th>
          </tr>
          <tr>
            <th style={thBlank}>код</th><th style={thBlank}>назва різниці</th><th style={thBlank}>сума</th>
            <th style={thBlank}>код</th><th style={thBlank}>назва різниці</th><th style={thBlank}>сума</th>
          </tr>
        </thead>
        <tbody>
          {sections.flatMap((s) => {
            const n = Math.max(s.inc.length, s.dec.length);
            return [
              <tr key={`${s.section}-h`}>
                <td colSpan={6} style={{ ...nameCell, fontWeight: 700, textAlign: 'center', background: print ? '#fff' : '#f8f8f6' }}>{s.section}</td>
              </tr>,
              ...Array.from({ length: n }).map((_, i) => (
                <tr key={`${s.section}-${i}`}>{cell(s.inc[i])}{cell(s.dec[i])}</tr>
              )),
            ];
          })}
          <tr style={{ fontWeight: 700 }}>
            <td style={numCell}>01</td><td style={nameCell}>Усього різниць, на які збільшується фінансовий результат</td>
            <td style={valCell}><SumCell value={ri.R001G3} readOnly print={print} /></td>
            <td style={numCell}>02</td><td style={nameCell}>Усього різниць, на які зменшується фінансовий результат</td>
            <td style={valCell}><SumCell value={ri.R002G3} readOnly print={print} /></td>
          </tr>
          <tr style={{ fontWeight: 700 }}>
            <td style={numCell}>03</td>
            <td style={nameCell} colSpan={4}>Різниці, які виникають відповідно до Податкового кодексу України (рядок 01 − рядок 02) (+, −) — переноситься до рядка 03 РІ декларації</td>
            <td style={valCell}><SumCell value={ri.R003G3} readOnly print={print} /></td>
          </tr>
        </tbody>
      </table>
      {doc && <SignaturesBlock doc={doc} org={org} print={print} withInn={false} />}
    </div>
  );
}
